import { reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isValidEmail } from '@/shared/utils/validation';
export function useCreateContactForm() {
    const { t } = useI18n();
    const form = reactive({
        salutation: '',
        firstName: '',
        lastName: '',
        mainEmail: '',
        mainPhone: '',
        contactType: 'Individual',
        sharedEmail: false,
    });
    // Individual-only fields
    const variantIndividual = reactive({
        category: '',
        source: '',
    });
    // Professional-only fields
    const variantPro = reactive({
        organisationId: '',
        position: '',
    });
    const firstNameError = ref('');
    const lastNameError = ref('');
    const mainEmailError = ref('');
    const contactTypeError = ref('');
    const phoneError = ref('');
    const organisationIdError = ref('');
    const positionError = ref('');
    // OP-5869: tracks whether OPAL's PhoneInput reports the typed number as valid, so the
    // deduplication search can fire on a valid phone alone (mirrors the valid-email trigger).
    const mainPhoneValid = ref(false);
    // Clear errors as user corrects fields
    watch(() => form.firstName, (val) => {
        if (val.length > 100)
            firstNameError.value = t('contact.create.validation.firstNameTooLong');
        else if (val.trim())
            firstNameError.value = '';
    });
    watch(() => form.lastName, (val) => {
        if (val.length > 100)
            lastNameError.value = t('contact.create.validation.lastNameTooLong');
        else if (val.trim())
            lastNameError.value = '';
    });
    watch(() => form.mainEmail, () => { mainEmailError.value = ''; });
    watch(() => form.mainPhone, (val) => {
        if (!val) {
            phoneError.value = '';
            mainPhoneValid.value = false;
        }
    });
    watch(() => form.contactType, (val) => {
        if (val)
            contactTypeError.value = '';
        resetVariantFields(val);
    });
    watch(() => variantPro.organisationId, (val) => { if (val)
        organisationIdError.value = ''; });
    watch(() => variantPro.position, (val) => { if (val.trim())
        positionError.value = ''; });
    // Blur handlers — trigger field-level validation on focus-out
    function onFirstNameBlur() {
        if (!form.firstName.trim())
            firstNameError.value = t('contact.create.validation.firstNameRequired');
        else if (form.firstName.length > 100)
            firstNameError.value = t('contact.create.validation.firstNameTooLong');
    }
    function onLastNameBlur() {
        if (!form.lastName.trim())
            lastNameError.value = t('contact.create.validation.lastNameRequired');
        else if (form.lastName.length > 100)
            lastNameError.value = t('contact.create.validation.lastNameTooLong');
    }
    // Email is optional — only validate format when a value is present
    function onMainEmailBlur() {
        if (form.mainEmail && !isValidEmail(form.mainEmail)) {
            mainEmailError.value = t('contact.create.validation.emailInvalid');
        }
    }
    function onOrganisationBlur() {
        if (form.contactType === 'Professional' && !variantPro.organisationId) {
            organisationIdError.value = t('contact.create.validation.organisationRequired');
        }
    }
    // Position (fonction) is required for a Professional contact (OP-5641).
    function onPositionBlur() {
        if (form.contactType === 'Professional' && !variantPro.position.trim()) {
            positionError.value = t('contact.create.validation.positionRequired');
        }
    }
    // Phone validation is delegated to OPAL's PhoneInput via @validation-change
    function onPhoneValidationChange(valid) {
        if (form.mainPhone && !valid) {
            phoneError.value = t('contact.create.validation.phoneInvalid');
        }
        else {
            phoneError.value = '';
        }
        mainPhoneValid.value = !!form.mainPhone && valid;
    }
    function resetVariantFields(type) {
        if (type === 'Individual') {
            variantPro.organisationId = '';
            variantPro.position = '';
            organisationIdError.value = '';
            positionError.value = '';
        }
        else {
            variantIndividual.category = '';
            variantIndividual.source = '';
        }
    }
    function validateRequired() {
        let valid = true;
        // Salutation, Category and Source are optional (OP-5641) — only First/Last name,
        // contact Type, and (for Professional) Organisation + Position gate the submit.
        if (!form.firstName.trim()) {
            firstNameError.value = t('contact.create.validation.firstNameRequired');
            valid = false;
        }
        else if (form.firstName.length > 100) {
            firstNameError.value = t('contact.create.validation.firstNameTooLong');
            valid = false;
        }
        if (!form.lastName.trim()) {
            lastNameError.value = t('contact.create.validation.lastNameRequired');
            valid = false;
        }
        else if (form.lastName.length > 100) {
            lastNameError.value = t('contact.create.validation.lastNameTooLong');
            valid = false;
        }
        if (!form.contactType) {
            contactTypeError.value = t('contact.create.validation.contactTypeRequired');
            valid = false;
        }
        if (form.mainEmail && !isValidEmail(form.mainEmail)) {
            mainEmailError.value = t('contact.create.validation.emailInvalid');
            valid = false;
        }
        if (form.contactType === 'Professional') {
            if (!variantPro.organisationId) {
                organisationIdError.value = t('contact.create.validation.organisationRequired');
                valid = false;
            }
            if (!variantPro.position.trim()) {
                positionError.value = t('contact.create.validation.positionRequired');
                valid = false;
            }
        }
        if (phoneError.value) {
            valid = false;
        }
        return valid;
    }
    function resetForm() {
        form.salutation = '';
        form.firstName = '';
        form.lastName = '';
        form.mainEmail = '';
        form.mainPhone = '';
        form.contactType = 'Individual';
        form.sharedEmail = false;
        firstNameError.value = '';
        lastNameError.value = '';
        mainEmailError.value = '';
        contactTypeError.value = '';
        phoneError.value = '';
        mainPhoneValid.value = false;
        variantIndividual.category = '';
        variantIndividual.source = '';
        variantPro.organisationId = '';
        variantPro.position = '';
        organisationIdError.value = '';
        positionError.value = '';
    }
    return {
        form,
        variantIndividual,
        variantPro,
        firstNameError,
        lastNameError,
        mainEmailError,
        contactTypeError,
        phoneError,
        mainPhoneValid,
        organisationIdError,
        positionError,
        onFirstNameBlur,
        onLastNameBlur,
        onMainEmailBlur,
        onOrganisationBlur,
        onPositionBlur,
        onPhoneValidationChange,
        validateRequired,
        resetForm,
    };
}
